import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, Check, X, Wrench, FileText, Terminal, Search, Globe, FolderOpen } from 'lucide-react';
import { useSessionStore } from '@/lib/store/sessionStore';
import { pendingApprovalsForSession } from '@/lib/approvals';
import { getTool } from '@/lib/tools/ToolRegistry';
import { CollapsibleCode } from './markdown/CollapsibleCode';

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  read_file: FileText,
  write_file: FileText,
  run_command: Terminal,
  run_tests: Terminal,
  grep: Search,
  web_search: Globe,
  read_url: Globe,
  browser: Globe,
  open_folder: FolderOpen,
};

export function ApprovalDialog() {
  const sessions = useSessionStore((s) => s.sessions);
  const activeId = useSessionStore((s) => s.activeId);
  const updateToolCall = useSessionStore((s) => s.updateToolCall);

  const session = sessions.find((s) => s.id === activeId);
  const pending = session ? pendingApprovalsForSession(session) : [];
  const head = pending[0];

  // dialog open state — auto-opens when there's a pending approval the user hasn't already dismissed
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  const open = !!head && head.toolCall.id !== dismissedFor;

  // when a new pending approval lands, clear the dismiss memory so it pops up
  useEffect(() => {
    if (!head) setDismissedFor(null);
  }, [head?.toolCall.id]);

  if (!head) return null;

  const def = getTool(head.toolCall.name);
  const Icon = ICONS[head.toolCall.name] ?? Wrench;
  const inputJson = safeStringify(head.toolCall.input);

  const onApprove = () => {
    updateToolCall(head.sessionId, head.messageId, head.toolCall.id, {
      status: 'success',
      output: 'Approved by user.',
      endedAt: Date.now(),
    });
    setDismissedFor(null);
  };

  const onReject = () => {
    updateToolCall(head.sessionId, head.messageId, head.toolCall.id, {
      status: 'error',
      error: 'Rejected by user.',
      endedAt: Date.now(),
    });
    setDismissedFor(null);
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) setDismissedFor(head.toolCall.id);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/15 animate-fade-in" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[min(560px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-canvas shadow-menu animate-fade-in"
          aria-describedby={undefined}
        >
          <div className="flex items-start gap-3 border-b border-border px-5 pb-4 pt-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-warning/10 text-warning">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[14px] font-semibold text-fg">
                Approve {def?.label ?? head.toolCall.name}?
              </Dialog.Title>
              {head.toolCall.approvalReason && (
                <p className="mt-1 text-[12.5px] leading-relaxed text-fg-muted">
                  {head.toolCall.approvalReason}
                </p>
              )}
            </div>
            {pending.length > 1 && (
              <span className="rounded-full bg-surface-2 px-2 py-0.5 text-2xs font-medium text-fg-muted">
                +{pending.length - 1} more pending
              </span>
            )}
          </div>

          <div className="space-y-3 px-5 py-4">
            <div>
              <div className="mb-1 flex items-center gap-1.5 text-2xs uppercase tracking-wide text-fg-subtle">
                <Icon className="h-3 w-3" />
                <span>Tool input</span>
              </div>
              <CollapsibleCode code={inputJson} language="json" maxLines={10} />
            </div>
          </div>

          <div className="flex items-center gap-2 border-t border-border px-5 py-3">
            <span className="text-2xs text-fg-subtle">
              Sensitive action · review before approving
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={onReject}
                className="focus-ring inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-[12.5px] font-medium text-fg hover:bg-surface-2"
              >
                <X className="h-3.5 w-3.5" />
                Reject
              </button>
              <button
                type="button"
                onClick={onApprove}
                className="focus-ring inline-flex items-center gap-1.5 rounded-md bg-fg px-3 py-1.5 text-[12.5px] font-medium text-canvas hover:bg-fg/90"
              >
                <Check className="h-3.5 w-3.5" />
                Approve
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
